import { useApolloClient } from '@apollo/client';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { appRoutes } from 'app/app-routes';
import Pagination from 'common/components/Pagination';
import SavedSearchIcons from 'common/components/SavedSearchIcons';
import { randomFromRange } from 'common/tools/random';
import { searchParamTools } from 'common/tools/search-param-tools';
import { PageStyles } from 'common/types/PageStyles';
import { SearchParams } from 'common/types/SearchParams';
import { LISTINGS_SEARCH_QUERY } from 'data/ListingsSearchQuery.graphql';
import { useListingsSearch } from 'data/useListingsSearch';
import { Listing, ListingsSearchQuery } from 'graphql/generated';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { searchParamActions, searchParamSelectors } from 'redux/search-param.slice';
import WsFlexbox from 'stories/layout/WsFlexbox';

import SearchChips from './SearchChips';
import SearchResultItem from './SearchResultItem';
import SortRange from './SortRange';

interface SavedSearchConfig {
  itemId: string;
  saved: boolean;
}

const SearchResults = () => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const navigate = useNavigate();
  const [pageParams] = useSearchParams();

  const listings = useSelector((state: RootState) => cacheSelectors.listings(state));
  const listingsCount = useSelector((state: RootState) => cacheSelectors.listingsCount(state));
  const searchError = useSelector((state: RootState) => cacheSelectors.searchError(state));
  const hasFilter = useSelector((state: RootState) => searchParamSelectors.filterCount(state));
  const urlParams = useSelector((state: RootState) => searchParamSelectors.urlParams(state));
  const searchParams = useSelector((state: RootState) => searchParamSelectors.searchParams(state));
  const searchChanged = useSelector((state: RootState) => searchParamSelectors.searchChanged(state));

  const user = useSelector((state: RootState) => cacheSelectors.user(state));
  const loading = false;
  const { buildFilter } = useListingsSearch();

  const [runOnce, runOnceTo] = React.useState(true);
  const [savedSearch, savedSearchTo] = React.useState(false);
  const [savedSearchConfig, savedSearchConfigTo] = React.useState<SavedSearchConfig | undefined>();
  const [savedSearchParamsJson, savedSearchParamsJsonTo] = React.useState('');

  const doSearch = useCallback(
    async (params: SearchParams, newQuery: boolean) => {
      // console.log('update', params);
      if (newQuery) dispatch(searchParamActions.set(params));
      else dispatch(searchParamActions.searchChanged(false));
      const queryParams = buildFilter(params);
      const { data } = await client.query<ListingsSearchQuery>({
        query: LISTINGS_SEARCH_QUERY,
        variables: {
          filters: queryParams.filters,
          skip: params.page * params.rowsPP,
          limit: params.rowsPP,
          sortKeys: queryParams.sortKeys,
        },
      });
      const listingsSearch = data.listingsSearch;
      dispatch(cacheActions.listingsCount(listingsSearch.aggregates.count ? listingsSearch.aggregates.count : 0));
      dispatch(cacheActions.listings(listingsSearch ? (listingsSearch.listings as Listing[]) : []));
    },
    [buildFilter, client, dispatch],
  );

  React.useEffect(() => {
    if (runOnce) {
      // console.log('run once');
      runOnceTo(false);
      const newParams = searchParamTools.toSearchParams(pageParams);
      doSearch(newParams, true);
    } else if (searchChanged) {
      // runOnceTo(true);
      navigate(`?${searchParamTools.toUrlParams(searchParams)}`);
      doSearch(searchParams, false);
    }
  }, [doSearch, searchParams, pageParams, runOnce, searchChanged, navigate]);

  useEffect(() => {
    // console.log('build config');
    const searchParamsJson = searchParamTools.toSaveJson(searchParams);
    savedSearchParamsJsonTo(searchParamsJson);
    if (!user) {
      savedSearchConfigTo(undefined);
    } else {
      const savedSearch = user.savedSearches?.find(x => x.searchParams === searchParamsJson);
      savedSearchConfigTo({
        itemId: savedSearch?.id || '',
        saved: savedSearch !== undefined,
      });
    }
  }, [user, searchParams]);

  const topLabel = () => {
    if (searchError)
      return (
        <Typography sx={{ color: 'red' }} variant="h5">
          Invalid Zip Code
        </Typography>
      );
    return (
      <Typography color="primary" sx={styles.header} variant="h5">
        {loading ? 'Loading...' : `${listingsCount} Search Results`}
      </Typography>
    );
  };

  const getImage = () => {
    return `ads/ad${randomFromRange(1, 22)}.png`;
  };

  return (
    <Box sx={styles.root}>
      {true ? (
        <>
          <Grid container item>
            <Grid container item xs={6} direction="column">
              {topLabel()}
              <Box sx={{ flexGrow: 1 }} />
              {hasFilter > 0 && (
                <Typography
                  color="primary"
                  variant="subtitle2"
                  sx={{ paddingLeft: 1.5, marginTop: 0, marginBottom: 0.5, fontSize: 13 }}
                >
                  Active Filters:
                </Typography>
              )}
            </Grid>
            <Grid container item xs={6} direction="column">
              {<Pagination />}
              {<SortRange />}
            </Grid>
            <Grid item xs={10}>
              <SearchChips />
            </Grid>
            <Grid container item xs={2} paddingRight={1} alignItems="flex-start" justifyContent="flex-end">
              {hasFilter > 0 && (
                <SavedSearchIcons
                  value={savedSearchConfig?.saved || false}
                  toggleFn={savedSearchTo}
                  itemId={savedSearchConfig?.itemId || ''}
                  saveSearchParams={searchParams}
                  user={user}
                  includeLabel={true}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <WsFlexbox direction="column" gap={2}>
                <WsFlexbox centered sx={{ marginTop: 0 }}>
                  <img style={{ height: '90%', width: '90%' }} src={getImage()} alt="ad" />
                </WsFlexbox>
                {listings.map((x, idx) => (
                  <SearchResultItem key={x.id} listing={x} />
                ))}
                <WsFlexbox centered sx={{ marginTop: 0 }}>
                  <img style={{ height: '90%', width: '90%' }} src={getImage()} alt="ad" />
                </WsFlexbox>
              </WsFlexbox>
            </Grid>
            <WsFlexbox sx={{ justifyContent: 'flex-end' }}>
              <Pagination />
            </WsFlexbox>
          </Grid>
        </>
      ) : (
        <Typography color="primary" sx={styles.header} variant="h5">
          Loading...
        </Typography>
      )}
    </Box>
  );
};

const styles: PageStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: 1,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      padding: 1,
    },
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    height: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
    padding: 1.5,
  },
  header: {
    paddingTop: 1,
    paddingLeft: 1,
    fontWeight: 600,
  },
  pagination: {
    padding: 1,
    alignItems: 'flex-end',
  },
};

export default SearchResults;
